import Box from "@mui/material/Box";
import List from "@mui/material/List";
import { useEffect, useState } from "react";
import UppercaseButton from "../../components/UppercaseButton/UppercaseButton";
import CompactListItem from "../../components/CompactListItem/CompactListItem";
import ParentRoleItem from "./ParentRoleItem/ParentRoleItem";
import ParentRoleCatalogueItem from "./ParentRoleCatalogueItem/ParentRoleCatalogueItem";
import ParentRoleDialog from "./ParentRoleDialog/ParentRoleDialog";
import Searchbar from "../../components/Searchbar/Searchbar";
import Paper from "../../components/Paper/Paper";
import ViewSelector from "../../components/ViewSelector/ViewSelector";
import {
  PARENT_ROLES_ENDPOINT,
  postRequest,
  getRequest,
  PARENT_ROLES_OVERVIEW_ENDPOINT,
  ROLES_PRIVILEGES_OVERVIEW_ENDPOINT,
  putRequest,
  deleteRequest,
} from "../../ApiService";
import { ParentRole } from "./Models/ParentRole";
import { ParentRoleOverview } from "./Models/ParentRoleOverview";
import { ParentRoleHierarchyOverview } from "./Models/ParentRoleHierarchyOverview";
import {
  ERROR_MESSAGE,
  ERROR_TITLE,
  PARENT_ROLE_CATALOGUE,
  PARENT_ROLE_CREATE,
  PARENT_ROLE_CREATE_ERROR,
  PARENT_ROLE_OVERVIEW,
  PARENT_ROLE_SEARCH,
  PARENT_ROLE_UPDATE_ERROR,
} from "../../utility/TextLabels";
import ErrorMessage from "../../components/ErrorMessage/ErrorMessage";

const ParentRolesPage = () => {
  const [isRoleDialogOpen, setIsRoleDialogOpen] = useState(false);
  const [selected, setSelected] = useState(PARENT_ROLE_OVERVIEW.toLowerCase());
  const [isErrorMessageOpen, setIsErrorMessageOpen] = useState(false);
  const [errorMessageText, setErrorMessageText] = useState("");

  const handleSelected = (newSelected: any) => {
    if (newSelected !== null) {
      setSelected(newSelected);
    }
  };

  const [parentRoleOverview, setParentRoleOverview] = useState<
    ParentRoleOverview[]
  >([]);

  const [parentRoleHierarchyOverview, setRoleHierarchyOverview] = useState<
    ParentRoleHierarchyOverview[]
  >([]);

  useEffect(() => {
    if (selected === PARENT_ROLE_OVERVIEW.toLowerCase()) {
      getParentRoleOverview();
    } else if (selected === PARENT_ROLE_CATALOGUE.toLowerCase()) {
      getRoleHierarchyOverview();
    }
  }, [selected]);

  const getParentRoleOverview = async (searchValue?: string) => {
    try {
      await getRequest(PARENT_ROLES_OVERVIEW_ENDPOINT, searchValue ?? "").then(
        (fetchedParentRoles: ParentRoleOverview[]) => {
          setParentRoleOverview(fetchedParentRoles);
        }
      );
    } catch (error) {
      setErrorMessageText(ERROR_MESSAGE);
      setIsErrorMessageOpen(true);
    }
  };

  const getRoleHierarchyOverview = async (searchValue?: string) => {
    try {
      await getRequest(
        ROLES_PRIVILEGES_OVERVIEW_ENDPOINT,
        searchValue ?? ""
      ).then((fetchedParentRoleHierarchy: ParentRoleHierarchyOverview[]) => {
        setRoleHierarchyOverview(fetchedParentRoleHierarchy);
      });
    } catch (error) {
      setErrorMessageText(ERROR_MESSAGE);
      setIsErrorMessageOpen(true);
    }
  };

  const createParentRole = async (parentRole: ParentRole) => {
    try {
      await postRequest(PARENT_ROLES_ENDPOINT, parentRole);
    } catch (error) {
      setErrorMessageText(PARENT_ROLE_CREATE_ERROR);
      setIsErrorMessageOpen(true);
    }
  };

  const updateParentRole = async (
    parentRole: ParentRole,
    parentRoleId: string
  ) => {
    try {
      await putRequest(PARENT_ROLES_ENDPOINT, parentRole, parentRoleId);
    } catch (error) {
      setErrorMessageText(PARENT_ROLE_UPDATE_ERROR);
      setIsErrorMessageOpen(true);
    }
    await getParentRoleOverview();
  };

  const deleteParentRole = async (id: string) => {
    try {
      await deleteRequest(PARENT_ROLES_ENDPOINT, id);
    } catch (error) {
      setErrorMessageText(ERROR_MESSAGE);
      setIsErrorMessageOpen(true);
    }
    await getParentRoleOverview();
  };

  const createRoleAndUpdateOverview = async (parentRole: ParentRole) => {
    await createParentRole(parentRole);
    await getParentRoleOverview();
  };

  return (
    <>
      <ViewSelector
        value={selected}
        onChange={(event, newSelected) => handleSelected(newSelected)}
        exclusive
        views={[PARENT_ROLE_OVERVIEW, PARENT_ROLE_CATALOGUE]}
      />

      <ErrorMessage
        isOpen={isErrorMessageOpen}
        primaryText={ERROR_TITLE}
        additionalText={errorMessageText}
        onClose={() => setIsErrorMessageOpen(false)}
      ></ErrorMessage>

      <Paper>
        {selected === PARENT_ROLE_OVERVIEW.toLowerCase() ? (
          <>
            <Box display="flex" justifyContent="space-between">
              <Searchbar
                placeholder={PARENT_ROLE_SEARCH}
                onChange={getParentRoleOverview}
              />
              <UppercaseButton
                color="primary"
                onClick={() => setIsRoleDialogOpen(true)}
              >
                {PARENT_ROLE_CREATE}
              </UppercaseButton>
              <ParentRoleDialog
                title={PARENT_ROLE_CREATE}
                isOpen={isRoleDialogOpen}
                onClose={() => setIsRoleDialogOpen(false)}
                onSubmit={createRoleAndUpdateOverview}
              />
            </Box>
            <List>
              {parentRoleOverview.map((role, index) => (
                <CompactListItem
                  key={index}
                  start={index === 0}
                  end={index === parentRoleOverview.length - 1}
                >
                  <ParentRoleItem
                    id={role.id}
                    name={role.name}
                    onUpdateParentRole={(updatedParentRole) =>
                      updateParentRole(updatedParentRole, role.id)
                    }
                    onDeleteParentRole={() => deleteParentRole(role.id)}
                  />
                </CompactListItem>
              ))}
            </List>
          </>
        ) : (
          <>
            <Searchbar
              placeholder={PARENT_ROLE_SEARCH}
              onChange={getRoleHierarchyOverview}
            />
            <List>
              {parentRoleHierarchyOverview.map((role, index) => (
                <CompactListItem
                  key={index}
                  start={index === 0}
                  end={index === parentRoleHierarchyOverview.length - 1}
                >
                  <ParentRoleCatalogueItem {...role} />
                </CompactListItem>
              ))}
            </List>
          </>
        )}
      </Paper>
    </>
  );
};

export default ParentRolesPage;
